import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { CompetitorTypes } from "~enums/competitorTypes";
import { useApi } from "~hooks/apiHooks/useApi";
import { useNotification } from "~hooks/useNotification";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import type { UpsertPlacingModel } from "~pages/FinalStandings/types/upsertPlacingModel";
import type { Placing } from "~types/placing";
import type { PlacingEntityResponse } from "~types/placingEntity";
import type { PlacingPersonResponse } from "~types/placingPerson";

export const useUpsertPlacing = () => {
  const { sport, organizationId, seasonId } = useParams();
  const request = useApi();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { competitorType } = useSportConfiguration();
  const notification = useNotification();

  let entrantUrlPart = "entities";
  if (competitorType === CompetitorTypes.Person) {
    entrantUrlPart = "persons";
  }

  return useMutation<PlacingEntityResponse | PlacingPersonResponse, Error, UpsertPlacingModel>({
    mutationFn: async (upsertModel: UpsertPlacingModel) => {
      let url = `${sport}/o/${organizationId}/seasons/${seasonId}/${entrantUrlPart}/placings`;
      if (upsertModel.placingId) {
        url = `${sport}/o/${organizationId}/seasons/${entrantUrlPart}/placings/${upsertModel.placingId}`;
      }
      const method = upsertModel.placingId ? "PUT" : "POST";
      const body = upsertModel.upsertData;
      return await request(url, { method, body });
    },
    onSuccess: async (response: { data: Array<Placing> }) => {
      if (response?.data && response.data.length > 0) {
        await queryClient.invalidateQueries({
          queryKey: ["final-standings", sport, organizationId, seasonId],
        });
        notification.success({
          message: formatMessage({ id: "placing.update.success" }),
          placement: "bottomRight",
        });
      }
    },
  });
};
